import { FormatHelpers } from "../helpers/format-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { BidAskDataModel } from "../models/data-point/bid-ask-data.model";

export const enum ColoredLastPriceSource {
    Chart = 0,
    AdvancedOEP = 1
}

export class ColoredLastPriceComponent {
    private lastPrice = 0;
    private currentSymbol = '';
    private lastBidAsk: BidAskDataModel | null = null;

    private readonly defaultUpColor = 'rgb(0, 255, 0)';
    private readonly defaultDownColor = 'rgb(255, 0, 0)';

    private chartStyle: ChartStyleSettingsModel | null = null;

    constructor(
        private element: HTMLElement | null,
        private source: ColoredLastPriceSource,
        private base: IChartContainer) {
        this.subscribeChartService();
    }

    private subscribeChartService() {
        this.base.chartService.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            this.chartStyle = chartStyle;
        });
        this.base.chartService.subscribeLastPriceData((lastPrice: number) => {
            this.setPrice(lastPrice);
        });
        this.base.chartService.subscribe_BidAsk((data: BidAskDataModel) => {
            this.lastBidAsk = data;
            const shouldUpdate = this.shouldUseCurrentTNSPrice() && this.symbolMatches(data.symbol);
            if (shouldUpdate) {
                this.setPrice(data.tradePrice);
            }
        });
    }

    private shouldUseCurrentTNSPrice(): boolean {
        return (this.source === ColoredLastPriceSource.AdvancedOEP && !!this.chartStyle?.advancedOEPUseCurrentTNSPrice)
            || (this.source === ColoredLastPriceSource.Chart && !!this.chartStyle?.chartLastUseCurrentTNSPrice);
    }

    private symbolMatches(symbol: string): boolean {
        // permissive when the window symbol isn't known yet (first load / marketing chart)
        return !this.currentSymbol || !symbol || symbol === this.currentSymbol;
    }

    public getLastPrice(): number {
        return this.lastPrice;
    }

    public getLastPriceToString(): string {
        const dicimalPlaces = this.getDecimalPlaces(this.lastPrice);
        return FormatHelpers.numberToStringThousandSeparators(this.lastPrice, false, dicimalPlaces);
    }

    public reset(newSymbol: string = ''): void {
        this.currentSymbol = newSymbol;
        this.lastPrice = 0;

        // if we already have a live trade price for the new symbol, show it immediately instead of blanking until the next feed
        const showCached = !!newSymbol && this.shouldUseCurrentTNSPrice() && this.lastBidAsk?.symbol === newSymbol && this.lastBidAsk.tradePrice > 0;
        if (showCached) {
            this.setPrice(this.lastBidAsk!.tradePrice);
        } else if (this.element) {
            this.element.innerHTML = '';
        }

    }

    public setPrice(newLastPrice: number) {
        // ignore non-positive/invalid prices (e.g. transient 0/NaN pushed right after reset) so display stays blank until real data arrives
        if (!(newLastPrice > 0)) {
            return;
        }

        let result = '$';

        const dicimalPlaces = this.getDecimalPlaces(newLastPrice);
        let lastPriceString = `${FormatHelpers.numberToStringThousandSeparators(this.lastPrice, false, dicimalPlaces)}`;
        let newLastPriceString = `${FormatHelpers.numberToStringThousandSeparators(newLastPrice, false, dicimalPlaces)}`;

        if (this.lastPrice === 0 || lastPriceString === newLastPriceString || !this.chartStyle) {
            result += newLastPriceString;
        } else {
            // Normalizing the length of both strings to be the same by prefixing with zeros
            const maxLength = Math.max(lastPriceString.length, newLastPriceString.length);
            const paddedOriginal = lastPriceString.padStart(maxLength, '0');
            const paddedNew = newLastPriceString.padStart(maxLength, '0');
            const upColor = this.chartStyle?.getChangeFromCloseUpColor() ?? '';
            const downColor = this.chartStyle?.getChangeFromCloseDownColor() ?? '';

            let color = '';
            if (this.lastPrice > newLastPrice) {
                color = downColor ? downColor : this.defaultDownColor;
            } else {
                color = upColor ? upColor : this.defaultUpColor;
            }

            let alreadyChanged = false;
            for (let i = 0; i < maxLength; i++) {
                if (paddedOriginal[i] === paddedNew[i] && !alreadyChanged) {
                    result += `${paddedNew[i]}`;
                } else {
                    result += `<span style="color:${color}">${paddedNew[i]}</span>`;
                    alreadyChanged = true;
                }
            }
        }

        this.lastPrice = newLastPrice;

        if (this.element) {
            this.element.innerHTML = result;
        }
    }

    private getDecimalPlaces(price: number): number {
        const dicimalPlaces = (this.chartStyle && price != 0 && price <= this.chartStyle.advancedOEPTOSFourDecimalLessThan) ? 4 : 2;
        return dicimalPlaces;
    }
}
